import { Box, Flex, Text, useColorModeValue } from "@chakra-ui/react";

const BANNER_TONES = {
    info: { bg: "rgba(33,150,243,0.1)", border: "rgba(33,150,243,0.3)", color: "rgba(100,181,246,1)" },
    danger: { bg: "rgba(244,67,54,0.1)", border: "rgba(244,67,54,0.3)", color: "rgba(229,115,115,1)" },
};

/**
 * Colored info/danger banner used across group dialogs and tabs.
 * @param {Object} props
 * @param {"info"|"danger"} [props.tone] - Visual tone (defaults to info/blue)
 * @param {React.ReactNode} props.children - Banner content
 * @returns {React.ReactElement}
 */
export function InfoBanner({ tone = "info", children }) {
    const t = BANNER_TONES[tone] || BANNER_TONES.info;
    return (
        <Box p="12px" borderRadius="8px" bg={t.bg} border="1px solid" borderColor={t.border}
            color={t.color} fontSize="0.78rem" fontWeight="600">
            {children}
        </Box>
    );
}

/**
 * Uppercase section label reused in detail tabs and the member form.
 * @param {Object} props
 * @param {React.ReactNode} props.children - Label text
 * @returns {React.ReactElement}
 */
export function SectionTitle({ children }) {
    const color = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Text color={color} fontSize="0.7rem" fontWeight="700" textTransform="uppercase"
            letterSpacing="0.5px" mb="8px">{children}</Text>
    );
}

/**
 * Key/value row used in the profile and wallet tabs.
 * @param {Object} props
 * @param {string} props.label - Left-side label
 * @param {*} props.value - Right-side value (falls back to em dash)
 * @param {string} [props.valueColor] - Optional override for the value color
 * @returns {React.ReactElement}
 */
export function KvRow({ label, value, valueColor }) {
    const keyColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const defaultValue = useColorModeValue("secondaryGray.900", "white");
    return (
        <Flex justify="space-between" gap="16px" py="6px" borderBottom="1px solid" borderColor="whiteAlpha.100">
            <Text fontSize="0.75rem" color={keyColor}>{label}</Text>
            <Text fontSize="0.8rem" fontWeight="600" textAlign="right" wordBreak="break-word"
                color={valueColor || defaultValue}>{value ?? "—"}</Text>
        </Flex>
    );
}

/**
 * Centered muted empty-state message.
 * @param {Object} props
 * @param {React.ReactNode} props.children - Message content
 * @returns {React.ReactElement}
 */
export function EmptyState({ children }) {
    const color = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return <Box py="24px" textAlign="center" color={color} fontSize="0.85rem">{children}</Box>;
}
